(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const revealSelectors = [
    'main h1', 'main h2', 'main h3',
    'main .rte', 'main p',
    'main .button', 'main a.button-unstyled',
    'main .product-card', 'main .card', 'main .collection-card',
  ].join(', ');

  function initReveal() {
    // Policy pages (Privacy/Terms/Shipping/Returns) are long, dense legal
    // text — fading each heading/paragraph in one at a time as you scroll
    // is disorienting there, so that content is excluded and shown in full
    // immediately instead.
    const targets = Array.from(document.querySelectorAll(revealSelectors)).filter(
      (el) => !el.closest('.kooshy-policy-main')
    );
    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('kooshy-reveal--visible'));
      return;
    }

    targets.forEach((el) => el.classList.add('kooshy-reveal'));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const delay = Math.min((i % 6) * 60, 300);
            el.style.transitionDelay = `${delay}ms`;
            el.classList.add('kooshy-reveal--visible');
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    targets.forEach((el) => observer.observe(el));
  }

  function initDividerReveal() {
    const line = document.querySelector('[data-testid^="divider-kooshy_divider"] .divider__line');
    if (!line) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      line.classList.add('kooshy-divider--visible');
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            line.classList.add('kooshy-divider--visible');
            observer.unobserve(line);
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(line);
  }

  // Editorial images use a CSS ambient-breathing keyframe (see kooshy-custom.css)
  // instead of scroll-driven parallax, so they feel alive at rest too, not just while scrolling.

  function initLoader() {
    const loader = document.getElementById('kooshy-loader');
    const bar = document.getElementById('kooshy-loader-bar');
    if (!loader) return;

    // Every internal link is a normal full-page navigation in Shopify (no
    // client-side routing), so this script re-runs on every page load. Gate
    // it with a session flag so the loader only ever appears once per
    // browsing session — on the first page opened, not on every page after.
    try {
      if (window.sessionStorage.getItem('kooshy-loaded') === '1') {
        loader.classList.add('kooshy-loader--hidden');
        return;
      }
      window.sessionStorage.setItem('kooshy-loaded', '1');
    } catch (e) {
      // sessionStorage unavailable (e.g. private browsing) — fall through
      // and show the loader as normal.
    }

    const MIN_VISIBLE_MS = 350;
    let shownAt = Date.now();
    let progressTimer = null;

    function startProgress() {
      if (!bar) return;
      bar.classList.remove('kooshy-loader__bar-fill--done');
      let pct = 0;
      bar.style.width = '0%';
      window.clearInterval(progressTimer);
      progressTimer = window.setInterval(() => {
        // Ease toward 85%, never completing on its own — real completion
        // snaps it to 100% when the page is actually ready.
        pct += (85 - pct) * 0.1;
        bar.style.width = `${Math.min(pct, 85)}%`;
      }, 200);
    }

    function finishProgress() {
      if (!bar) return;
      window.clearInterval(progressTimer);
      bar.classList.add('kooshy-loader__bar-fill--done');
      bar.style.width = '100%';
    }

    function hide() {
      const elapsed = Date.now() - shownAt;
      const wait = Math.max(0, MIN_VISIBLE_MS - elapsed);
      finishProgress();
      window.setTimeout(() => {
        loader.classList.add('kooshy-loader--hidden');
      }, wait);
    }

    startProgress();

    if (document.readyState === 'complete') {
      hide();
    } else {
      window.addEventListener('load', hide, { once: true });
    }

    // Loader only ever shows on the initial page load, not on internal
    // navigation to products or any other page.
  }

  // Policy pages (Privacy/Terms/Shipping/Returns) share one Liquid template
  // (templates/policy.liquid) whose body is whatever raw HTML is stored on
  // shop.policy.body — plain <h2>/<p>/<ul> with no structure to hook numbered
  // sections or a sidebar TOC onto. This walks that flat markup once on load,
  // groups it into numbered <article> sections keyed off each <h2>, and
  // builds the matching sidebar links, so every policy gets the same
  // hero/sidebar/numbered-article treatment without hardcoding any content.
  function initPolicyTOC() {
    const body = document.getElementById('kooshy-policy-body');
    const tocNav = document.getElementById('kooshy-policy-toc-nav');
    const toc = document.querySelector('.kooshy-policy-toc');
    if (!body || !tocNav) return;

    const rte = body.querySelector('.rte');
    if (!rte) return;

    // Some policy content (e.g. Shopify's auto-generated policy text) comes
    // wrapped in a single extra <div> instead of flat <h2>/<p> siblings.
    // Unwrap it so the heading-splitting below still finds the headings.
    let source = rte;
    if (rte.children.length === 1 && rte.children[0].tagName === 'DIV') {
      source = rte.children[0];
    }

    const nodes = Array.from(source.childNodes);
    const groups = [];
    let current = null;

    nodes.forEach((node) => {
      const isHeading = node.nodeType === 1 && node.tagName === 'H2';
      if (isHeading) {
        current = { heading: node, content: [] };
        groups.push(current);
        return;
      }
      // Skip empty whitespace-only text nodes that sit between block elements.
      if (node.nodeType === 3 && !node.textContent.trim()) return;
      if (!current) {
        current = { heading: null, content: [] };
        groups.push(current);
      }
      current.content.push(node);
    });

    if (!groups.length) return;

    const fragment = document.createDocumentFragment();

    groups.forEach((group, i) => {
      const headingText = group.heading ? group.heading.textContent.trim() : 'Overview';
      const id = `policy-section-${i}`;

      const article = document.createElement('article');
      article.className = 'kooshy-policy-article';
      article.id = id;

      const head = document.createElement('div');
      head.className = 'kooshy-policy-article__head';

      const num = document.createElement('span');
      num.className = 'kooshy-policy-article__num';
      num.textContent = String(i).padStart(2, '0');
      head.appendChild(num);

      const h2 = document.createElement('h2');
      h2.textContent = headingText;
      head.appendChild(h2);

      article.appendChild(head);
      group.content.forEach((node) => article.appendChild(node));

      const isContactSection = /contact/i.test(headingText);
      if (isContactSection && i === groups.length - 1) {
        article.classList.add('kooshy-policy-article--contact');
        const cta = document.createElement('a');
        cta.className = 'kooshy-policy-cta';
        cta.href = '/pages/contact';
        cta.textContent = 'Get in touch';
        article.appendChild(cta);
      }

      fragment.appendChild(article);

      const link = document.createElement('a');
      link.href = `#${id}`;
      link.textContent = headingText;
      tocNav.appendChild(link);
    });

    rte.innerHTML = '';
    rte.appendChild(fragment);

    if (toc && 'IntersectionObserver' in window && !reduceMotion) {
      const links = Array.from(tocNav.querySelectorAll('a'));
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const link = tocNav.querySelector(`a[href="#${entry.target.id}"]`);
            if (!link) return;
            if (entry.isIntersecting) {
              links.forEach((l) => l.classList.remove('kooshy-policy-toc__link--active'));
              link.classList.add('kooshy-policy-toc__link--active');
            }
          });
        },
        { rootMargin: '-15% 0px -70% 0px' }
      );
      body.querySelectorAll('.kooshy-policy-article').forEach((el) => observer.observe(el));
    }
  }

  function initMagneticButtons() {
    if (reduceMotion) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const strength = 0.3;
    const maxOffset = 10;

    document.querySelectorAll('.button-custom').forEach((btn) => {
      if (btn.closest('[id$="__editorial_statement"], [id$="__editorial_jumbo"]')) return;
      if (btn.classList.contains('add-to-cart-button')) return;
      btn.addEventListener('mousemove', (event) => {
        const rect = btn.getBoundingClientRect();
        const relX = event.clientX - (rect.left + rect.width / 2);
        const relY = event.clientY - (rect.top + rect.height / 2);
        const offsetX = Math.max(-maxOffset, Math.min(maxOffset, relX * strength));
        const offsetY = Math.max(-maxOffset, Math.min(maxOffset, relY * strength));
        btn.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.transform = '';
      });
    });
  }

  // Color swatch tooltip. The swatch row lives inside an <overflow-list>
  // component with overflow-y: hidden (for horizontal scrolling), which
  // clips any tooltip positioned as a CSS-only descendant of the swatch —
  // confirmed via inspecting the live page. So this is one shared element
  // appended directly to <body> (outside that clipped container) and
  // positioned with getBoundingClientRect() on hover/focus instead.
  function initSwatchTooltip() {
    const tooltip = document.createElement('div');
    tooltip.className = 'kooshy-swatch-tooltip';
    tooltip.setAttribute('role', 'tooltip');
    document.body.appendChild(tooltip);

    function showTooltip(target) {
      const text = target.getAttribute('data-tooltip');
      if (!text) return;
      tooltip.textContent = text;
      const rect = target.getBoundingClientRect();
      tooltip.style.left = `${rect.left + rect.width / 2}px`;
      tooltip.style.top = `${rect.top - 10}px`;
      tooltip.classList.add('kooshy-swatch-tooltip--visible');
    }

    function hideTooltip() {
      tooltip.classList.remove('kooshy-swatch-tooltip--visible');
    }

    // mouseover/mouseout (not mouseenter/mouseleave) since delegation
    // needs bubbling, and this needs to keep working after Shopify morphs
    // in fresh swatch markup (e.g. after switching another option).
    document.addEventListener('mouseover', (event) => {
      const target = event.target.closest && event.target.closest('.kooshy-color-circle[data-tooltip]');
      if (target) showTooltip(target);
    });
    document.addEventListener('mouseout', (event) => {
      const target = event.target.closest && event.target.closest('.kooshy-color-circle[data-tooltip]');
      if (target) hideTooltip();
    });
    document.addEventListener('focusin', (event) => {
      const label = event.target.closest && event.target.closest('.variant-option__button-label');
      const target = label && label.querySelector('.kooshy-color-circle[data-tooltip]');
      if (target) showTooltip(target);
    });
    document.addEventListener('focusout', (event) => {
      const label = event.target.closest && event.target.closest('.variant-option__button-label');
      const target = label && label.querySelector('.kooshy-color-circle[data-tooltip]');
      if (target) hideTooltip();
    });
  }

  function initSizeGuide() {
    // Delegated on document (rather than bound to specific elements) so it
    // keeps working after the variant picker re-renders on a variant/colour
    // change, without needing to re-initialise anything.
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-size-guide-open]');
      if (opener) {
        const dialog = document.getElementById(opener.getAttribute('data-size-guide-open'));
        if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
        return;
      }

      const closer = event.target.closest('[data-size-guide-close]');
      if (closer) {
        const dialog = closer.closest('dialog');
        if (dialog) dialog.close();
        return;
      }

      // Clicking the backdrop (the <dialog> element itself, outside its
      // content box) closes it, same as native <dialog> click-outside behaviour.
      if (event.target.tagName === 'DIALOG' && event.target.classList.contains('kooshy-size-guide-dialog')) {
        event.target.close();
      }
    });
  }

  function initHeroScrollCue() {
    const hero = document.querySelector('[id*="__hero_jVaWmY"] .hero');
    if (!hero) return;

    const section = hero.closest('.shopify-section');
    const nextSection = section ? section.nextElementSibling : null;
    if (!nextSection) return;

    const cue = document.createElement('button');
    cue.type = 'button';
    cue.className = 'kooshy-hero-scroll-cue';
    cue.setAttribute('aria-label', 'Scroll to explore');
    cue.innerHTML =
      '<svg width="20" height="12" viewBox="0 0 20 12" fill="none" aria-hidden="true">' +
      '<path d="M1 1L10 10L19 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';

    cue.addEventListener('click', () => {
      nextSection.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    });

    hero.appendChild(cue);
  }

  // Solid-white header while the mobile drawer is open. The CSS
  // #header-component:has(#Details-menu-drawer-container[open]) rule
  // covers this in browsers that support :has(), but some in-app/webview
  // browsers (and the admin theme-editor preview panel) don't — this class
  // toggle is the reliable fallback for those.
  function initHeaderMenuOpenState() {
    const details = document.getElementById('Details-menu-drawer-container');
    const header = document.getElementById('header-component');
    if (!details || !header) return;

    const sync = () => header.classList.toggle('kooshy-menu-open', details.open);
    details.addEventListener('toggle', sync);
    sync();
  }

  function initAddToCartFlyAnimation() {
    if (reduceMotion) return;

    document.addEventListener('submit', (event) => {
      const form = event.target.closest && event.target.closest('form[data-type="add-to-cart-form"]');
      if (!form) return;

      const cartIcon = document.querySelector('.action__cart');
      if (!cartIcon) return;

      // Look for a nearby product image: the quick-add modal, the product
      // page's main content, or (fallback) anywhere reasonable on the page.
      const container =
        form.closest('[class*="quick-add"]') ||
        form.closest('[class*="modal"]') ||
        form.closest('main') ||
        document.body;
      const sourceImg =
        container.querySelector('slideshow-slide[aria-hidden="false"] img') ||
        container.querySelector('img');
      if (!sourceImg) return;

      const startRect = sourceImg.getBoundingClientRect();
      const endRect = cartIcon.getBoundingClientRect();
      if (startRect.width === 0 || startRect.height === 0) return;

      const flyer = document.createElement('img');
      flyer.src = sourceImg.currentSrc || sourceImg.src;
      flyer.setAttribute('aria-hidden', 'true');
      flyer.className = 'kooshy-cart-flyer';
      flyer.style.left = `${startRect.left}px`;
      flyer.style.top = `${startRect.top}px`;
      flyer.style.width = `${startRect.width}px`;
      flyer.style.height = `${startRect.height}px`;
      document.body.appendChild(flyer);

      const deltaX = endRect.left + endRect.width / 2 - (startRect.left + startRect.width / 2);
      const deltaY = endRect.top + endRect.height / 2 - (startRect.top + startRect.height / 2);

      requestAnimationFrame(() => {
        flyer.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(0.12) rotate(8deg)`;
        flyer.style.opacity = '0.3';
      });

      window.setTimeout(() => {
        flyer.remove();
        cartIcon.classList.add('kooshy-cart-bump');
        window.setTimeout(() => cartIcon.classList.remove('kooshy-cart-bump'), 350);
      }, 650);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initPolicyTOC();
      initReveal();
      initDividerReveal();
      initLoader();
      initMagneticButtons();
      initSwatchTooltip();
      initAddToCartFlyAnimation();
      initSizeGuide();
      initHeroScrollCue();
      initHeaderMenuOpenState();
    });
  } else {
    initPolicyTOC();
    initReveal();
    initDividerReveal();
    initLoader();
    initMagneticButtons();
    initSwatchTooltip();
    initAddToCartFlyAnimation();
    initSizeGuide();
    initHeroScrollCue();
    initHeaderMenuOpenState();
  }
})();
